import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import closeLineGreyIconUrl from '../../assets/icons/icon_close_line_grey.svg';
import logoUrl from '../../assets/images/lalafan_logo.png';

const ENTER_MS = 280;

export type ConfirmOptions = {
  logo?: boolean;
  /** 미지정 시 로컬 번들 로고(logo.png) 사용 */
  logoSrc?: string;
  style?: React.CSSProperties;
  textStyle?: React.CSSProperties;
  buttonStyle?: React.CSSProperties;
  buttonTextStyle?: React.CSSProperties;
  title?: string;
  message?: string;
  onConfirm: () => void;
  onClose?: () => void;
  onClosePress?: () => void;
  onlyConfirm?: boolean;
  children?: React.ReactNode;
  closeButton?: boolean;
  cancelButtonText?: string;
  confirmButtonText?: string;
};

const Backdrop = styled.div<{ $open: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition: opacity ${ENTER_MS}ms ease;
`;

const DialogContent = styled.div<{ $open: boolean }>`
  position: relative;
  max-width: calc(100vw - 40px);
  background-color: white;
  padding: 48px 20px 20px;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transform: scale(${({ $open }) => ($open ? 1 : 0.94)});
  transition:
    opacity ${ENTER_MS}ms ease,
    transform ${ENTER_MS}ms ease;
`;

const DialogTitle = styled.h2`
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 20px;
  text-align: center;
  letter-spacing: -0.4px;
`;

const DialogDescription = styled.p`
  font-size: 14px;
  margin-bottom: 20px;
  white-space: pre-line;
  text-align: center;
  letter-spacing: -0.4px;
`;

const DialogActions = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const DialogButton = styled.button`
  padding: 10px 20px;
  width: 100%;
  border-radius: 12px;
  background-color: black;
  color: white;
  cursor: pointer;
  border: none;
  outline: none;
  transition: all 0.3s ease;
  &:hover {
    opacity: 0.7;
  }
`;

const DialogButtonText = styled.span`
  font-size: 16px;
  font-weight: bold;
  color: #d3ee34;
`;

const DialogCloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  &:hover {
    opacity: 0.7;
  }
`;

const DialogCloseIconImg = styled.img`
  width: 24px;
  height: 24px;
  display: block;
`;

const DialogLogo = styled.div`
  text-decoration: none;
  flex-shrink: 0;
  margin-bottom: 8px;
`;

const DialogLogoImg = styled.img`
  display: block;
  width: 120px;
  height: auto;
  object-fit: contain;
  margin-bottom: 8px;
`;

const Confirm = ({
  logo = false,
  logoSrc,
  style,
  textStyle,
  buttonStyle,
  buttonTextStyle,
  title,
  message,
  onConfirm,
  onClose,
  onClosePress,
  onlyConfirm = false,
  children,
  closeButton,
  cancelButtonText,
  confirmButtonText,
}: ConfirmOptions) => {
  const [open, setOpen] = useState(false);
  const exitingRef = useRef(false);
  const afterExitRef = useRef<(() => void) | null>(null);
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setOpen(true));
    });
    return () => cancelAnimationFrame(id);
  }, []);

  const clearFallback = useCallback(() => {
    if (fallbackTimerRef.current != null) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  }, []);

  const runAfterExit = useCallback(() => {
    clearFallback();
    exitingRef.current = false;
    const fn = afterExitRef.current;
    afterExitRef.current = null;
    fn?.();
  }, [clearFallback]);

  const startExit = useCallback(
    (after: () => void) => {
      if (exitingRef.current) return;
      exitingRef.current = true;
      afterExitRef.current = after;
      setOpen(false);
      clearFallback();
      fallbackTimerRef.current = setTimeout(runAfterExit, ENTER_MS + 80);
    },
    [clearFallback, runAfterExit],
  );

  useEffect(() => () => clearFallback(), [clearFallback]);

  const handleBackdropClick = useCallback(() => {
    startExit(() => onClose?.());
  }, [onClose, startExit]);

  const handleContentClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleBackdropTransitionEnd = useCallback(
    (e: React.TransitionEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      if (e.propertyName !== 'opacity') return;
      if (open || !exitingRef.current) return;
      runAfterExit();
    },
    [open, runAfterExit],
  );

  const handleConfirmClick = useCallback(() => {
    startExit(() => onConfirm());
  }, [onConfirm, startExit]);

  const handleCancelClick = useCallback(() => {
    startExit(() => onClose?.());
  }, [onClose, startExit]);

  const handleCloseIconClick = useCallback(() => {
    startExit(() => (onClosePress ?? onClose)?.());
  }, [onClose, onClosePress, startExit]);

  return (
    <Backdrop
      $open={open}
      onClick={handleBackdropClick}
      onTransitionEnd={handleBackdropTransitionEnd}
      role="presentation"
    >
      <DialogContent
        $open={open}
        onClick={handleContentClick}
        role="dialog"
        aria-modal="true"
        style={style}
      >
        {logo && (
          <DialogLogo>
            <DialogLogoImg src={logoSrc ?? logoUrl} alt="라라팡 로고" />
          </DialogLogo>
        )}
        {closeButton && (
          <DialogCloseButton
            type="button"
            onClick={handleCloseIconClick}
            aria-label="닫기"
          >
            <DialogCloseIconImg src={closeLineGreyIconUrl} alt="" />
          </DialogCloseButton>
        )}
        <DialogTitle style={textStyle}>{title}</DialogTitle>
        <DialogDescription style={textStyle}>{message}</DialogDescription>
        {children}
        <DialogActions>
          {!onlyConfirm && (
            <DialogButton
              type="button"
              onClick={handleCancelClick}
              style={buttonStyle}
            >
              <DialogButtonText style={buttonTextStyle}>
                {cancelButtonText}
              </DialogButtonText>
            </DialogButton>
          )}
          <DialogButton
            type="button"
            onClick={handleConfirmClick}
            style={buttonStyle}
          >
            <DialogButtonText style={buttonTextStyle}>
              {confirmButtonText}
            </DialogButtonText>
          </DialogButton>
        </DialogActions>
      </DialogContent>
    </Backdrop>
  );
};
export default Confirm;
